import { useState } from 'react'
import { PageHeader, DataTable, Card, CardHeader, ErrorState, SkeletonKartu, StatCard, FilterBar, FILTER_KOSONG, type KolomTabel } from '@/components'
import type { NilaiFilter } from '@/components'
import { LaporanTampil } from '@/components/LaporanTampil'
import { formatAngka, formatRupiah } from '@/lib/format'
import { useApi, usePaged } from '@/lib/useApi'
import { useOpsiUnit } from '@/lib/opsi'
import { cariAngka } from '@/components/LaporanTampil'

interface BarisTunggakan {
  id: string
  invoiceNumber?: string
  studentNumber?: string
  studentName?: string
  unitName?: string
  dueDate?: string
  outstandingAmount: number
  umurHari?: number
  status?: string
  [kunci: string]: unknown
}

export default function TunggakanPage() {
  const opsiUnit = useOpsiUnit()
  const [filter, setFilter] = useState<NilaiFilter>({ ...FILTER_KOSONG, dari: `${new Date().getFullYear()}-01-01` })

  const daftar = usePaged<BarisTunggakan>('/arrears', {
    query: {
      dari: filter.dari || undefined,
      sampai: filter.sampai || undefined,
      unitId: filter.unitId || undefined,
    },
  })

  const aging = useApi<unknown>('/arrears/aging', {
    dari: filter.dari || undefined,
    sampai: filter.sampai || undefined,
    unitId: filter.unitId || undefined,
  })

  const kolom: KolomTabel<BarisTunggakan>[] = [
    {
      kunci: 'invoiceNumber',
      judul: 'Nomor Tagihan',
      render: (b) => <span className="font-medium text-netral-800">{b.invoiceNumber ?? '—'}</span>,
    },
    {
      kunci: 'studentName',
      judul: 'Santri/Siswa',
      render: (b) => (
        <div>
          <p className="text-netral-800">{b.studentName ?? '—'}</p>
          <p className="text-xs text-netral-500">{b.studentNumber ?? ''}</p>
        </div>
      ),
    },
    { kunci: 'unitName', judul: 'Unit', render: (b) => b.unitName ?? '—' },
    { kunci: 'dueDate', judul: 'Jatuh Tempo', render: (b) => b.dueDate ?? '—' },
    {
      kunci: 'umurHari',
      judul: 'Umur (hari)',
      rata: 'kanan',
      render: (b) => (typeof b.umurHari === 'number' ? formatAngka(b.umurHari) : '—'),
    },
    {
      kunci: 'outstandingAmount',
      judul: 'Sisa Tagihan',
      rata: 'kanan',
      render: (b) => <span className="font-medium text-red-700">{formatRupiah(b.outstandingAmount)}</span>,
    },
  ]

  const bucket = [
    { label: '0–30 hari', kunci: ['b0_30', 'sampai30', 'hari0_30', 'bucket0_30'] },
    { label: '31–60 hari', kunci: ['b31_60', 'sampai60', 'hari31_60', 'bucket31_60'] },
    { label: '61–90 hari', kunci: ['b61_90', 'sampai90', 'hari61_90', 'bucket61_90'] },
    { label: '> 90 hari', kunci: ['b90plus', 'lebih90', 'hari90plus', 'bucket90_plus', 'diAtas90'] },
  ]

  return (
    <div>
      <PageHeader
        judul="Tunggakan"
        deskripsi="Daftar piutang tagihan yang belum lunas beserta analisis umur piutang (aging)."
        ekspor={{
          path: '/reports/arrears-aging',
          namaDasar: 'tunggakan-aging',
          query: { dari: filter.dari || undefined, sampai: filter.sampai || undefined, unitId: filter.unitId || undefined },
        }}
      />

      <FilterBar
        nilai={filter}
        onUbah={setFilter}
        unit={opsiUnit}
        tampilkan={{ kategori: false, program: false, fundId: false }}
      />

      <div className="mb-5">
        <Card>
          <CardHeader
            judul="Rentang Umur Piutang"
            deskripsi="Pengelompokan sisa tagihan berdasarkan jumlah hari sejak jatuh tempo."
          />
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {aging.memuat && !aging.data ? (
              <div className="lg:col-span-4">
                <SkeletonKartu jumlah={4} />
              </div>
            ) : aging.galat ? (
              <div className="lg:col-span-4">
                <ErrorState pesan={aging.galat} kode={aging.kodeGalat} onCoba={aging.muat} ringkas />
              </div>
            ) : (
              bucket.map((b) => (
                <StatCard
                  key={b.label}
                  label={b.label}
                  nilai={cariAngka(aging.data, b.kunci)}
                  formatNilai={(v) => (v === null || v === undefined ? '—' : formatRupiah(Number(v)))}
                  nada="merah"
                />
              ))
            )}
          </div>
          <div className="mt-4">
            <LaporanTampil
              data={aging.data}
              memuat={false}
              galat={null}
              kosong={<p className="py-4 text-center text-sm text-netral-500">Data umur piutang belum tersedia.</p>}
            />
          </div>
        </Card>
      </div>

      <DataTable
        kolom={kolom}
        data={daftar.items}
        kunciBaris={(b, i) => b.id ?? String(i)}
        memuat={daftar.memuat}
        galat={daftar.galat}
        kodeGalat={daftar.kodeGalat}
        onCoba={daftar.muat}
        meta={daftar.meta}
        onHalaman={daftar.setHalaman}
        onUrut={daftar.setUrutan}
        urutSaatIni={daftar.urutan}
        pesanKosong="Tidak ada tunggakan pada filter ini."
      />
    </div>
  )
}
